import { Pin, Settings2 } from '@myosh/icons';
import {
  Button,
  Checkbox,
  cn,
  Field,
  FieldLabel,
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
  Toggle,
} from '@myosh/odin-components';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ColumnSettings as Settings, useColumnSettings } from '@/hooks/use-column-settings';

export interface SettingsColumn {
  id: string;
  label: string;
  defaultVisible: boolean;
}

const resolveSettings = (columns: SettingsColumn[], settings: Settings): Settings => ({
  visibility: Object.fromEntries(
    columns.map((column) => [column.id, settings.visibility[column.id] ?? column.defaultVisible])
  ),
  pinned: columns.filter((column) => settings.pinned.includes(column.id)).map((column) => column.id),
});

const isSameSettings = (left: Settings, right: Settings) =>
  left.pinned.join() === right.pinned.join() &&
  Object.entries(left.visibility).every(([id, visible]) => right.visibility[id] === visible);

// Hiding a column unpins it and pinning one shows it, so a pinned column is never hidden.
export const ColumnSettings = ({ columns }: { columns: SettingsColumn[] }) => {
  const { t } = useTranslation();
  const checkboxId = useId();
  const { settings, saveSettings } = useColumnSettings();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => resolveSettings(columns, settings));

  const current = resolveSettings(columns, settings);
  const allVisible = columns.every((column) => draft.visibility[column.id]);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraft(current);
    }
    setOpen(next);
  };

  const setVisible = (id: string, visible: boolean) =>
    setDraft((previous) => ({
      visibility: { ...previous.visibility, [id]: visible },
      pinned: visible ? previous.pinned : previous.pinned.filter((pinnedId) => pinnedId !== id),
    }));

  const setAllVisible = (visible: boolean) =>
    setDraft((previous) => ({
      visibility: Object.fromEntries(columns.map((column) => [column.id, visible])),
      pinned: visible ? previous.pinned : [],
    }));

  const togglePinned = (id: string) =>
    setDraft((previous) =>
      previous.pinned.includes(id)
        ? { ...previous, pinned: previous.pinned.filter((pinnedId) => pinnedId !== id) }
        : {
            visibility: { ...previous.visibility, [id]: true },
            pinned: columns
              .filter((column) => column.id === id || previous.pinned.includes(column.id))
              .map((column) => column.id),
          }
    );

  const handleApply = () => {
    saveSettings(draft);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger render={<Button variant="outline" size="sm" />}>
        <Settings2 data-icon="inline-start" />
        {t(($) => $.visibleColumns)}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <PopoverTitle>{t(($) => $.visibleColumns)}</PopoverTitle>
        <Field orientation="horizontal">
          <Checkbox id={checkboxId} checked={allVisible} onChange={(event) => setAllVisible(event.target.checked)} />
          <FieldLabel htmlFor={checkboxId} className="font-normal">
            {t(($) => $.showAll)}
          </FieldLabel>
        </Field>
        <ul className="flex max-h-80 flex-col gap-1 overflow-auto border-t pt-2">
          {columns.map((column) => {
            const isPinned = draft.pinned.includes(column.id);
            const columnCheckboxId = `${checkboxId}-${column.id}`;
            return (
              <li key={column.id} className="flex items-center justify-between gap-2">
                <Field orientation="horizontal">
                  <Checkbox
                    id={columnCheckboxId}
                    checked={draft.visibility[column.id] ?? false}
                    onChange={(event) => setVisible(column.id, event.target.checked)}
                  />
                  <FieldLabel htmlFor={columnCheckboxId} className="font-normal">
                    {column.label}
                  </FieldLabel>
                </Field>
                <Toggle
                  size="sm"
                  pressed={isPinned}
                  onPressedChange={() => togglePinned(column.id)}
                  aria-label={`${t(($) => $.pin)} ${column.label}`}
                >
                  <Pin className={cn(isPinned && 'fill-current')} />
                </Toggle>
              </li>
            );
          })}
        </ul>
        <Button onClick={handleApply} disabled={isSameSettings(draft, current)}>
          {t(($) => $.apply)}
        </Button>
      </PopoverContent>
    </Popover>
  );
};
